export interface ApiVariant {
    url: string;
    width: number;
    height: number;
}

export interface ApiImage {
    thumbnail: ApiVariant;
    gallery: ApiVariant;
}

export type Availability = "in_stock" | "made_to_order";

export interface ApiItem {
    id: string;
    name: string;
    description: string;
    availability: Availability;
    priceCents: number | null;
    salePriceCents: number | null;
    coverIndex: number;
    images: ApiImage[];
}

export interface ApiContact {
    label: string;
    url: string;
}

export interface ApiCatalog {
    site: {
        title: string;
        tagline: string;
        currency: string;
        contacts: ApiContact[];
    };
    items: ApiItem[];
}

function invalid(path: string, expectation: string): never {
    throw new Error(`Invalid catalog response: ${path} ${expectation}`);
}

function requireRecord(
    value: unknown,
    path: string,
): Record<string, unknown> {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        invalid(path, "must be an object");
    }

    return value as Record<string, unknown>;
}

function requireString(
    record: Record<string, unknown>,
    key: string,
    path: string,
): string {
    const value = record[key];

    if (typeof value !== "string" || value.length === 0) {
        invalid(`${path}.${key}`, "must be a non-empty string");
    }

    return value;
}

function requireInteger(
    record: Record<string, unknown>,
    key: string,
    path: string,
    minimum: number,
): number {
    const value = record[key];

    if (
        typeof value !== "number" ||
        !Number.isSafeInteger(value) ||
        value < minimum
    ) {
        invalid(
            `${path}.${key}`,
            `must be an integer greater than or equal to ${minimum}`,
        );
    }

    return value;
}

function requireAvailability(
    record: Record<string, unknown>,
    path: string,
): Availability {
    const value = requireString(
        record,
        "availability",
        path,
    );

    if (value !== "in_stock" && value !== "made_to_order") {
        invalid(
            `${path}.availability`,
            'must be "in_stock" or "made_to_order"',
        );
    }

    return value;
}

function requireNullableInteger(
    record: Record<string, unknown>,
    key: string,
    path: string,
    minimum: number,
): number | null {
    if (record[key] === null) {
        return null;
    }

    return requireInteger(record, key, path, minimum);
}

function parseVariant(
    value: unknown,
    path: string,
): ApiVariant {
    const record = requireRecord(value, path);
    const url = requireString(record, "url", path);

    if (!url.startsWith("/media/")) {
        invalid(`${path}.url`, "must begin with /media/");
    }

    return {
        url,
        width: requireInteger(record, "width", path, 1),
        height: requireInteger(record, "height", path, 1),
    };
}

function parseImage(
    value: unknown,
    path: string,
): ApiImage {
    const record = requireRecord(value, path);

    return {
        thumbnail: parseVariant(
            record.thumbnail,
            `${path}.thumbnail`,
        ),
        gallery: parseVariant(
            record.gallery,
            `${path}.gallery`,
        ),
    };
}

function parseItem(
    value: unknown,
    index: number,
): ApiItem {
    const path = `items[${index}]`;
    const record = requireRecord(value, path);
    const imageValues = record.images;

    if (!Array.isArray(imageValues) || imageValues.length === 0) {
        invalid(`${path}.images`, "must be a non-empty array");
    }

    const images = imageValues.map((image, imageIndex) =>
        parseImage(image, `${path}.images[${imageIndex}]`)
    );

    const coverIndex = requireInteger(
        record,
        "coverIndex",
        path,
        0,
    );

    if (coverIndex >= images.length) {
        invalid(`${path}.coverIndex`, "must reference an image");
    }

    const priceCents = requireNullableInteger(
        record,
        "priceCents",
        path,
        0,
    );
    const salePriceCents = requireNullableInteger(
        record,
        "salePriceCents",
        path,
        0,
    );

    if (salePriceCents !== null) {
        if (priceCents === null) {
            invalid(
                `${path}.salePriceCents`,
                "requires a regular price",
            );
        }

        if (salePriceCents >= priceCents) {
            invalid(
                `${path}.salePriceCents`,
                "must be less than priceCents",
            );
        }
    }

    return {
        id: requireString(record, "id", path),
        name: requireString(record, "name", path),
        description: requireString(record, "description", path),
        availability: requireAvailability(record, path),
        priceCents,
        salePriceCents,
        coverIndex,
        images,
    };
}

function parseContact(
    value: unknown,
    index: number,
): ApiContact {
    const path = `site.contacts[${index}]`;
    const record = requireRecord(value, path);
    const url = requireString(record, "url", path);

    let parsedUrl: URL;

    try {
        parsedUrl = new URL(url);
    } catch {
        invalid(`${path}.url`, "must be an absolute URL");
    }

    if (
        parsedUrl.protocol !== "https:" ||
        parsedUrl.username !== "" ||
        parsedUrl.password !== ""
    ) {
        invalid(
            `${path}.url`,
            "must be an HTTPS URL without credentials",
        );
    }

    return {
        label: requireString(record, "label", path),
        url: parsedUrl.href,
    };
}

export function parseCatalog(value: unknown): ApiCatalog {
    const root = requireRecord(value, "catalog");
    const site = requireRecord(root.site, "site");

    const currency = requireString(site, "currency", "site");

    if (!/^[A-Z]{3}$/.test(currency)) {
        invalid(
            "site.currency",
            "must be a three-letter uppercase currency code",
        );
    }

    if (!Array.isArray(site.contacts)) {
        invalid("site.contacts", "must be an array");
    }
    const contacts = site.contacts.map(parseContact);

    if (!Array.isArray(root.items)) {
        invalid("items", "must be an array");
    }

    return {
        site: {
            title: requireString(site, "title", "site"),
            tagline: requireString(site, "tagline", "site"),
            currency,
            contacts,
        },
        items: root.items.map(parseItem),
    };
}

export async function fetchCatalog(
    signal?: AbortSignal,
): Promise<ApiCatalog> {
    const response = await fetch(
        "/api/items",
        signal ? { signal } : undefined,
    );

    if (!response.ok) {
        throw new Error(
            `Catalog request failed with HTTP ${response.status}`,
        );
    }

    return parseCatalog(await response.json());
}
